import { useState } from 'react';
import { Settings, Copy, CheckCircle2 } from 'lucide-react';

export default function ENVConverter() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const envToYAML = (env: string): string => {
    const lines = env.trim().split('\n');
    let yaml = '';

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) {
        // Preserve comments
        if (trimmed.startsWith('#')) {
          yaml += trimmed + '\n';
        }
        return;
      }

      const [key, ...valueParts] = trimmed.split('=');
      let value = valueParts.join('=').trim();

      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }

      // Try to parse as number or boolean
      let parsedValue: any = value;
      if (value === 'true') parsedValue = 'true';
      else if (value === 'false') parsedValue = 'false';
      else if (!isNaN(Number(value)) && value !== '') parsedValue = value;
      else parsedValue = `"${value}"`;

      yaml += `${key.toLowerCase()}: ${parsedValue}\n`;
    });

    return yaml;
  };

  const convert = () => {
    setOutput(envToYAML(input));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <Settings className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">ENV to YAML Converter</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Convert .env files to YAML format</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
            ENV File
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`# Database Configuration
DB_HOST=localhost
DB_PORT=5432
DB_NAME=myapp
DB_USER=admin
DB_PASSWORD=secret123

# API Keys
API_KEY=abc123xyz
DEBUG=true`}
            className="w-full h-96 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent resize-none transition-all"
          />
          <button
            onClick={convert}
            className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
          >
            Convert to YAML
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              YAML Output
            </label>
            {output && (
              <button
                onClick={copyToClipboard}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
              >
                {copied ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            )}
          </div>
          <textarea
            value={output}
            readOnly
            placeholder="YAML output will appear here..."
            className="w-full h-96 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-dark-text resize-none"
          />
        </div>
      </div>
    </div>
  );
}
